import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Mail,
  RefreshCw,
  CheckCircle2,
  ArrowLeft,
  ArrowRight,
  CalendarDays,
  Clock,
  Search,
  LogOut,
  Loader2,
  AlertCircle,
  SkipForward,
  Target,
  PlayCircle,
  LayoutDashboard,
  BookOpen,
  Sparkles
} from 'lucide-react';
import { UserProfile, PraxisPaper } from '../types';
import { examCatalog, ExamCatalogEntry } from '../data/examCatalog';
import {
  auth,
  sendVerificationLinkToCurrentUser,
  refreshCurrentUserEmailVerified,
  saveOnboardingAnswers,
  requestWelcomeEmail,
  isUserSubscribed
} from '../lib/firebase';

// Same family/paper matching heuristic used by AuthModal.tsx, StudyGuides.tsx
// and ExamDetailPage.tsx, duplicated here (this codebase's established
// pattern) so the screener groups papers exactly the way the rest of the
// app does.
function paperMatchesExamCode(paper: PraxisPaper, targetExamCodeRaw: string): boolean {
  const paperExamCode = paper.examCode ? String(paper.examCode).trim() : '';
  const targetExamCode = targetExamCodeRaw.trim();
  if (paperExamCode && paperExamCode.toLowerCase() === targetExamCode.toLowerCase()) return true;
  if (paperExamCode) {
    const lowerP = paperExamCode.toLowerCase();
    const lowerTarget = targetExamCode.toLowerCase();
    if (lowerP.startsWith(lowerTarget + '-') || lowerP === lowerTarget) return true;
  }
  if (paper.code) {
    const lowerCode = paper.code.trim().toLowerCase();
    const lowerTarget = targetExamCode.toLowerCase();
    if (lowerCode.startsWith(lowerTarget + '-') || lowerCode === lowerTarget) return true;
  }
  if (targetExamCode === 'Praxis-Core' && paper.category === 'core' && (!paperExamCode || paperExamCode.startsWith('Praxis'))) return true;
  if (targetExamCode === 'Praxis-Elementary' && (paper.category === 'elementary' || paper.code === '5001')) return true;
  if (targetExamCode === 'Praxis-Subject' && (paper.category === 'subject' || paper.category === 'elementary')) return true;
  if (targetExamCode === 'Praxis-PLT' && paper.category === 'plt') return true;
  return false;
}

function papersForFamily(entry: ExamCatalogEntry, papers: PraxisPaper[]): PraxisPaper[] {
  const seen = new Set<string>();
  const out: PraxisPaper[] = [];
  for (const p of papers) {
    if (!p || !p.id || seen.has(p.id)) continue;
    if ((p as any).visibleToStudents === false) continue;
    if (entry.examCodes.some((code) => paperMatchesExamCode(p, code))) {
      seen.add(p.id);
      out.push(p);
    }
  }
  return out.sort((a, b) => String(a.code).localeCompare(String(b.code), undefined, { numeric: true }));
}

function paperQuestionCount(p: PraxisPaper): number {
  if (typeof p.questionCount === 'number' && p.questionCount > 0) return p.questionCount;
  if (typeof p.totalQuestions === 'number' && p.totalQuestions > 0) return p.totalQuestions;
  return 0;
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const DAILY_GOALS: { minutes: number; label: string }[] = [
  { minutes: 30, label: '30 min/day' },
  { minutes: 60, label: '1 hr/day' },
  { minutes: 120, label: '2 hrs/day' },
  { minutes: 180, label: '3+ hrs/day' }
];

const RESEND_COOLDOWN_SECONDS = 60;


export type OnboardingNextStep = 'practice' | 'exam' | 'dashboard';

interface PlanRow {
  when: string;
  title: string;
  detail: string;
}

function weekLabel(startWeek: number, endWeek: number): string {
  return startWeek >= endWeek ? `Week ${startWeek}` : `Weeks ${startWeek} to ${endWeek}`;
}

// Builds a simple, honest study plan from the paper's own domain weights,
// the exam date and the daily goal. Nothing here is invented: every row is
// derived from data the student just gave us or from the paper's blueprint.
function buildStudyPlan(
  paper: PraxisPaper | null,
  examDateIso: string | null,
  dailyMinutes: number
): { rows: PlanRow[]; daysLeft: number | null; totalHours: number; weeks: number } {
  let daysLeft: number | null = null;
  if (examDateIso) {
    const exam = new Date(`${examDateIso}T00:00:00`);
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    daysLeft = Math.max(1, Math.round((exam.getTime() - start.getTime()) / 86400000));
  }
  const planDays = daysLeft ?? 42; // no date yet: a 6 week starter plan
  const weeks = Math.max(1, Math.ceil(planDays / 7));
  const totalHours = Math.round((planDays * dailyMinutes) / 60);

  const domains = (paper && Array.isArray(paper.domains) ? paper.domains : [])
    .filter((d: any) => d && d.name)
    .map((d: any) => ({ name: String(d.name), weight: Number(d.weightPercentage) || 0 }));
  const weightSum = domains.reduce((a, d) => a + d.weight, 0);
  const ordered = [...domains].sort((a, b) => b.weight - a.weight);

  const rows: PlanRow[] = [];

  if (weeks <= 2) {
    // Short runway: diagnose, focus, then simulate.
    rows.push({
      when: 'First 2 days',
      title: 'Find your starting point',
      detail: 'Answer a short practice set across every topic so you know where you stand.'
    });
    rows.push({
      when: 'Middle days',
      title: ordered.length > 0 ? `Focus on ${ordered[0].name}` : 'Practice your weakest topics',
      detail: ordered.length > 0
        ? `It carries the most weight on the exam. Then move through ${ordered.slice(1).map((d) => d.name).join(', ') || 'the rest'}.`
        : 'Use your results to pick the topics that need the most work.'
    });
    rows.push({
      when: 'Last 3 days',
      title: 'Timed mock exam and light review',
      detail: 'Sit one full mock under real timing, review every miss, then rest before test day.'
    });
    return { rows, daysLeft, totalHours, weeks };
  }

  const finalWeeks = weeks >= 8 ? 2 : 1;
  const contentWeeks = Math.max(1, weeks - 1 - finalWeeks);
  const contentStartWeek = 2;

  rows.push({
    when: 'Week 1',
    title: 'Find your starting point',
    detail: 'Answer a short practice set across every topic so you know where to spend your time.'
  });

  if (ordered.length > 0 && weightSum > 0 && contentWeeks >= ordered.length) {
    // Enough weeks to give every domain its own whole weeks: share them out
    // by weight (largest remainder, at least one week each).
    const raw = ordered.map((d) => (d.weight / weightSum) * contentWeeks);
    const alloc = raw.map((r) => Math.max(1, Math.floor(r)));
    let spare = contentWeeks - alloc.reduce((a, n) => a + n, 0);
    const byRemainder = raw.map((r, i) => ({ i, rem: r - Math.floor(r) })).sort((a, b) => b.rem - a.rem);
    for (let k = 0; spare > 0 && byRemainder.length > 0; k = (k + 1) % byRemainder.length, spare--) alloc[byRemainder[k].i]++;
    while (spare < 0) {
      const big = alloc.indexOf(Math.max(...alloc));
      if (alloc[big] <= 1) break;
      alloc[big]--;
      spare++;
    }
    let week = contentStartWeek;
    ordered.forEach((d, i) => {
      const hours = Math.max(1, Math.round((alloc[i] * 7 * dailyMinutes) / 60));
      rows.push({
        when: weekLabel(week, week + alloc[i] - 1),
        title: d.name,
        detail: `${Math.round(d.weight)}% of the exam. About ${hours} hours of practice questions, flashcards and the study guide.`
      });
      week += alloc[i];
    });
  } else if (ordered.length > 0 && weightSum > 0) {
    // Fewer weeks than domains: spread the days in proportion to weight.
    const contentDays = contentWeeks * 7;
    let cursor = 0;
    ordered.forEach((d, i) => {
      const share = d.weight / weightSum;
      const days = i === ordered.length - 1 ? contentDays - cursor : Math.max(1, Math.round(share * contentDays));
      const startDay = cursor;
      const endDay = Math.min(contentDays, cursor + days) - 1;
      cursor = Math.min(contentDays, cursor + days);
      const startWeek = contentStartWeek + Math.floor(startDay / 7);
      const endWeek = contentStartWeek + Math.floor(Math.max(startDay, endDay) / 7);
      const hours = Math.max(1, Math.round((days * dailyMinutes) / 60));
      rows.push({
        when: weekLabel(startWeek, endWeek),
        title: d.name,
        detail: `${Math.round(d.weight)}% of the exam. About ${hours} ${hours === 1 ? 'hour' : 'hours'} of practice questions, flashcards and the study guide.`
      });
    });
  } else {
    rows.push({
      when: weekLabel(contentStartWeek, contentStartWeek + contentWeeks - 1),
      title: 'Practice topic by topic',
      detail: 'Work through each topic with practice questions, flashcards and the study guide, starting with your weakest.'
    });
  }

  const finalStart = contentStartWeek + contentWeeks;
  rows.push({
    when: weekLabel(finalStart, weeks),
    title: 'Timed mock exams and review',
    detail: 'Sit full mock exams under real timing, then review every question you missed. Rest the day before your exam.'
  });

  return { rows, daysLeft, totalHours, weeks };
}

export interface OnboardingResult {
  targetPaperId?: string;
  targetExamFamily: string;
  targetExamDate: string | null;
  dailyGoalMinutes?: number;
  onboardingCompletedAt: string;
  skipped: boolean;
}

interface OnboardingFlowProps {
  userProfile: UserProfile;
  papers: PraxisPaper[];
  needsVerification: boolean;
  isNewAccount: boolean;
  onVerified: () => void;
  onComplete: (result: OnboardingResult, next?: OnboardingNextStep) => void;
  onSignOut: () => void;
  // Paper a guest was trying when they signed up from the free preview.
  presetPaperId?: string | null;
}

type Step = 'exam' | 'date' | 'goal' | 'plan';

export const OnboardingFlow: React.FC<OnboardingFlowProps> = ({
  userProfile,
  papers,
  needsVerification,
  isNewAccount,
  onVerified,
  onComplete,
  onSignOut,
  presetPaperId
}) => {
  const email = auth.currentUser?.email || userProfile.email || '';

  // ---------- Verification view ----------
  const [checking, setChecking] = useState(false);
  const [cooldown, setCooldown] = useState<number>(RESEND_COOLDOWN_SECONDS);
  const [verifyMsg, setVerifyMsg] = useState<string | null>(null);
  const [verifyErr, setVerifyErr] = useState<string | null>(null);

  useEffect(() => {
    if (!needsVerification) return;
    let cancelled = false;
    const check = async () => {
      try {
        const ok = await refreshCurrentUserEmailVerified();
        if (ok && !cancelled) onVerified();
      } catch {
        /* network blips are fine, the next poll retries */
      }
    };
    check();
    const poll = window.setInterval(check, 5000);
    return () => {
      cancelled = true;
      window.clearInterval(poll);
    };
  }, [needsVerification]);

  useEffect(() => {
    if (!needsVerification || cooldown <= 0) return;
    const t = window.setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => window.clearTimeout(t);
  }, [needsVerification, cooldown]);

  const handleCheckNow = async () => {
    setChecking(true);
    setVerifyErr(null);
    setVerifyMsg(null);
    try {
      const ok = await refreshCurrentUserEmailVerified();
      if (ok) {
        onVerified();
      } else {
        setVerifyErr("We can't see the verification yet. Click the link in the email, then try again.");
      }
    } catch (err: any) {
      setVerifyErr(err?.message || 'Could not check right now. Please try again.');
    } finally {
      setChecking(false);
    }
  };

  const handleResend = async () => {
    setVerifyErr(null);
    setVerifyMsg(null);
    try {
      await sendVerificationLinkToCurrentUser();
      setVerifyMsg(`A new link is on its way to ${email}.`);
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err: any) {
      if (err?.code === 'auth/too-many-requests') {
        setVerifyErr('Too many requests. Please wait a few minutes before asking for another link.');
      } else {
        setVerifyErr(err?.message || 'Could not resend the link. Please try again.');
      }
      setCooldown(RESEND_COOLDOWN_SECONDS);
    }
  };

  // ---------- Screener ----------
  const [step, setStep] = useState<Step>('exam');
  const [familyId, setFamilyId] = useState<string | null>(null);
  const [paperId, setPaperId] = useState<string | null>(null);
  const [paperSearch, setPaperSearch] = useState('');

  const today = new Date();
  const [month, setMonth] = useState<string>('');
  const [day, setDay] = useState<string>('');
  const [year, setYear] = useState<string>('');
  const [noDateYet, setNoDateYet] = useState(false);

  const [dailyGoal, setDailyGoal] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState<string | null>(null);
  const [savedResult, setSavedResult] = useState<OnboardingResult | null>(null);

  // Start with the paper they were already trying, once the list is in.
  const presetApplied = useRef(false);
  useEffect(() => {
    if (presetApplied.current || !presetPaperId || papers.length === 0) return;
    const target = papers.find((p) => p.id === presetPaperId || p.code === presetPaperId);
    if (!target) return;
    const fam = examCatalog.find((e) => papersForFamily(e, papers).some((p) => p.id === target.id));
    if (!fam) return;
    presetApplied.current = true;
    setFamilyId(fam.id);
    setPaperId(target.id);
  }, [presetPaperId, papers]);

  const family = familyId ? examCatalog.find((e) => e.id === familyId) || null : null;
  const familyPapers = useMemo(() => (family ? papersForFamily(family, papers) : []), [family, papers]);
  const filteredPapers = useMemo(() => {
    const q = paperSearch.trim().toLowerCase();
    if (!q) return familyPapers;
    return familyPapers.filter((p) => `${p.code} ${p.title}`.toLowerCase().includes(q));
  }, [familyPapers, paperSearch]);
  const selectedPaper = paperId ? familyPapers.find((p) => p.id === paperId) || null : null;

  const daysInMonth = month && year ? new Date(Number(year), Number(month), 0).getDate() : 31;
  const yearOptions = [today.getFullYear(), today.getFullYear() + 1, today.getFullYear() + 2];

  const examDateIso = !noDateYet && month && day && year
    ? `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
    : null;
  const examDateInPast = (() => {
    if (!examDateIso) return false;
    const picked = new Date(`${examDateIso}T23:59:59`);
    return picked.getTime() < Date.now();
  })();

  const canContinueExam = !!family && (familyPapers.length === 0 || !!selectedPaper);
  const canContinueDate = noDateYet || (!!examDateIso && !examDateInPast);

  const finish = async (skipped: boolean) => {
    setSaving(true);
    setSaveErr(null);
    const nowIso = new Date().toISOString();
    const result: OnboardingResult = skipped
      ? { targetExamFamily: familyId || '', targetExamDate: null, onboardingCompletedAt: nowIso, skipped: true }
      : {
          targetPaperId: selectedPaper ? selectedPaper.id : (family ? family.id : undefined),
          targetExamFamily: family ? family.id : '',
          targetExamDate: noDateYet ? null : examDateIso,
          dailyGoalMinutes: dailyGoal || undefined,
          onboardingCompletedAt: nowIso,
          skipped: false
        };
    try {
      await saveOnboardingAnswers(userProfile.uid, {
        targetPaperId: result.targetPaperId,
        targetExamFamily: result.targetExamFamily || undefined,
        targetExamDate: skipped ? undefined : result.targetExamDate,
        dailyGoalMinutes: result.dailyGoalMinutes,
        onboardingCompletedAt: result.onboardingCompletedAt
      });
      if (isNewAccount) {
        // Fire and forget: the server decides whether to send (once only).
        requestWelcomeEmail();
      }
      if (skipped) {
        onComplete(result, 'dashboard');
      } else {
        // Show the finished plan and the next steps before leaving.
        setSavedResult(result);
        setSaving(false);
        setStep('plan');
        window.scrollTo?.(0, 0);
      }
    } catch (err: any) {
      console.error('[Onboarding] Could not save answers:', err);
      setSaveErr('We could not save your answers. Please check your connection and try again.');
      setSaving(false);
    }
  };

  const stepIndex = step === 'exam' ? 0 : step === 'date' ? 1 : 2;
  const subscribed = isUserSubscribed(userProfile);
  const firstName = (userProfile.displayName || '').trim().split(/\s+/)[0] || '';
  const plan = useMemo(
    () => buildStudyPlan(selectedPaper, noDateYet ? null : examDateIso, dailyGoal || 60),
    [selectedPaper, noDateYet, examDateIso, dailyGoal]
  );
  const goalLabel = selectedPaper ? `${selectedPaper.code} ${selectedPaper.title}` : family?.shortLabel || 'your exam';
  const dailyLabel = DAILY_GOALS.find((g) => g.minutes === dailyGoal)?.label || '';

  // ---------- Shared chrome ----------
  // A plain render helper (not a nested component), so typing in the paper
  // search box never remounts the page and loses focus.
  const renderShell = (children: React.ReactNode, wide = false) => (
    <div className="fixed inset-0 z-[60] bg-gradient-to-b from-[#0f1b4d] to-[#131f5c] overflow-y-auto">
      <div className="absolute top-0 left-0 right-0 h-1.5 bg-[#0E9594]" />
      <div className={`${wide ? 'max-w-3xl' : 'max-w-2xl'} mx-auto px-4 sm:px-6 py-8 sm:py-12`}>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2.5">
            <span className="w-9 h-9 rounded-xl bg-[#0E9594] flex items-center justify-center text-white font-black text-sm">P</span>
            <span className="text-white text-lg font-extrabold leading-tight">Pre-Praxis PREP</span>
          </div>
          <button
            type="button"
            onClick={onSignOut}
            className="text-xs font-bold text-indigo-200 hover:text-white flex items-center gap-1.5 cursor-pointer"
          >
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </div>
        {children}
      </div>
    </div>
  );

  const renderPlan = () => {
    const result = savedResult as OnboardingResult;
    // A bundle (5001) has no questions of its own, so it opens the exam
    // page with its subtests instead of a practice session.
    const hasRealPaper = !!selectedPaper && !(Array.isArray(selectedPaper.bundleOf) && selectedPaper.bundleOf.length > 0);
    const resumingSamePaper = hasRealPaper && !!presetPaperId &&
      (selectedPaper!.id === presetPaperId || selectedPaper!.code === presetPaperId);
    const practiceLabel = hasRealPaper
      ? subscribed
        ? `Start practicing ${selectedPaper!.code}`
        : resumingSamePaper
          ? `Continue ${selectedPaper!.code} with 5 more free questions`
          : `Try 5 free questions on ${selectedPaper!.code}`
      : 'Open my exam page';
    const dateText = examDateIso && !noDateYet ? `${MONTHS[Number(month) - 1]} ${day}, ${year}` : 'No date yet';
    return renderShell(
      <div className="space-y-5">
        <div className="bg-white rounded-3xl shadow-2xl p-6 sm:p-8 space-y-6">
          <div className="space-y-2">
            <div className="inline-flex items-center gap-1.5 text-xs font-black tracking-[0.2em] text-[#0E9594]">
              <Sparkles className="w-3.5 h-3.5" /> YOUR PLAN IS READY
            </div>
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-[#0f1b4d] leading-tight">
              {firstName ? `You're all set, ${firstName}.` : "You're all set."}
            </h1>
            <p className="text-slate-600 text-sm sm:text-base">
              Here is how we will get you ready for <span className="font-bold text-slate-900">{goalLabel}</span>.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
            {[
              { label: 'Exam date', value: dateText },
              { label: 'Days to go', value: plan.daysLeft !== null ? String(plan.daysLeft) : 'Open' },
              { label: 'Daily goal', value: dailyLabel || 'Not set' },
              { label: plan.daysLeft !== null ? 'Study time ahead' : 'First 6 weeks', value: `${plan.totalHours} hrs` }
            ].map((t) => (
              <div key={t.label} className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                <div className="text-[10.5px] font-bold uppercase tracking-wider text-slate-500">{t.label}</div>
                <div className="text-sm sm:text-base font-extrabold text-[#0f1b4d] mt-0.5">{t.value}</div>
              </div>
            ))}
          </div>

          <div>
            <div className="text-sm font-extrabold text-[#0f1b4d] mb-3 flex items-center gap-2">
              <CalendarDays className="w-4 h-4 text-[#0E9594]" />
              {plan.daysLeft !== null ? `Your ${plan.weeks} week plan` : 'Your 6 week starter plan'}
            </div>
            <ol className="space-y-2.5">
              {plan.rows.map((r, i) => (
                <li key={`${r.when}-${i}`} className="flex gap-3 rounded-2xl border border-slate-200 p-3.5">
                  <span className="w-7 h-7 rounded-full bg-[#0f1b4d] text-white text-xs font-black flex items-center justify-center shrink-0">{i + 1}</span>
                  <div className="min-w-0">
                    <div className="text-[11px] font-black uppercase tracking-wider text-[#0E9594]">{r.when}</div>
                    <div className="text-sm font-extrabold text-slate-900">{r.title}</div>
                    <div className="text-xs text-slate-600 leading-relaxed mt-0.5">{r.detail}</div>
                  </div>
                </li>
              ))}
            </ol>
            {plan.daysLeft === null && (
              <p className="text-xs text-slate-500 mt-2">Add your exam date from your dashboard any time and the plan will fit around it.</p>
            )}
          </div>
        </div>

        <div className="bg-white rounded-3xl shadow-2xl p-6 sm:p-8 space-y-4">
          <div className="text-sm font-extrabold text-[#0f1b4d] flex items-center gap-2">
            <Target className="w-4 h-4 text-[#0E9594]" /> What's next
          </div>
          <button
            type="button"
            onClick={() => onComplete(result, hasRealPaper ? 'practice' : 'exam')}
            className="w-full bg-[#0E9594] hover:bg-[#0b7f7e] text-white font-black text-sm sm:text-base py-4 rounded-2xl transition shadow-md flex items-center justify-center gap-2 cursor-pointer"
          >
            <PlayCircle className="w-5 h-5" /> {practiceLabel}
          </button>
          {hasRealPaper && !subscribed && (
            <p className="text-xs text-slate-500 text-center -mt-1">
              No card needed. Unlock every question, mock exam and study guide whenever you are ready.
            </p>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {hasRealPaper && (
              <button
                type="button"
                onClick={() => onComplete(result, 'exam')}
                className="bg-white border border-slate-300 text-[#0f1b4d] hover:bg-slate-50 font-bold text-sm py-3.5 rounded-2xl flex items-center justify-center gap-2 cursor-pointer"
              >
                <BookOpen className="w-4 h-4" /> See mocks, guides and flashcards
              </button>
            )}
            <button
              type="button"
              onClick={() => onComplete(result, 'dashboard')}
              className={`bg-white border border-slate-300 text-[#0f1b4d] hover:bg-slate-50 font-bold text-sm py-3.5 rounded-2xl flex items-center justify-center gap-2 cursor-pointer ${hasRealPaper ? '' : 'sm:col-span-2'}`}
            >
              <LayoutDashboard className="w-4 h-4" /> Go to my dashboard
            </button>
          </div>
        </div>
      </div>,
      true
    );
  };

  if (needsVerification) {
    return (
      renderShell(
        <div className="bg-white rounded-3xl shadow-2xl p-6 sm:p-8 space-y-5">
          <div className="w-12 h-12 rounded-2xl bg-teal-50 border border-teal-200 flex items-center justify-center">
            <Mail className="w-6 h-6 text-[#0E9594]" />
          </div>
          <div className="space-y-2">
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-[#0f1b4d] leading-tight">Check your inbox</h1>
            <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
              We sent a verification link to <span className="font-bold text-slate-900">{email}</span>. Click the link in that
              email, then come back here. This page moves on by itself once you are verified.
            </p>
          </div>

          {verifyMsg && (
            <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 p-3 rounded-xl text-xs font-semibold flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 shrink-0" /> {verifyMsg}
            </div>
          )}
          {verifyErr && (
            <div className="bg-rose-50 border border-rose-200 text-rose-800 p-3 rounded-xl text-xs font-semibold flex items-start gap-2">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /> {verifyErr}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button
              type="button"
              onClick={handleCheckNow}
              disabled={checking}
              className="bg-[#0f1b4d] hover:bg-[#1b2f7a] text-white font-black text-sm py-3.5 rounded-2xl transition shadow-md flex items-center justify-center gap-2 cursor-pointer disabled:opacity-60"
            >
              {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              I've verified
            </button>
            <button
              type="button"
              onClick={handleResend}
              disabled={cooldown > 0}
              className="bg-white border border-slate-300 text-[#0f1b4d] hover:bg-slate-50 font-bold text-sm py-3.5 rounded-2xl transition flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw className="w-4 h-4" />
              {cooldown > 0 ? `Resend link in ${cooldown}s` : 'Resend link'}
            </button>
          </div>

          <p className="text-xs text-slate-500 leading-relaxed">
            Can't find it? Check your spam or promotions folder. Wrong email address? Sign out and create the account again with the right one.
          </p>
        </div>
      )
    );
  }

  if (step === 'plan' && savedResult) {
    return renderPlan();
  }

  return renderShell(
    <div className="bg-white rounded-3xl shadow-2xl p-6 sm:p-8">
      {/* Progress */}
      <div className="flex items-center gap-2 mb-6">
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className={`h-2 rounded-full transition-all ${i === stepIndex ? 'w-8 bg-[#0E9594]' : i < stepIndex ? 'w-2 bg-teal-300' : 'w-2 bg-slate-200'}`}
          />
        ))}
      </div>
      <div className="text-xs font-black tracking-[0.2em] text-[#0E9594] mb-2">STEP {stepIndex + 1} OF 3</div>

      {saveErr && (
        <div className="mb-4 bg-rose-50 border border-rose-200 text-rose-800 p-3 rounded-xl text-xs font-semibold flex items-start gap-2">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /> {saveErr}
        </div>
      )}

      {step === 'exam' && (
        <div className="space-y-6">
          <div className="space-y-2">
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-[#0f1b4d] leading-tight">Which exam are you preparing for?</h1>
            <p className="text-slate-600 text-sm sm:text-base">This shapes the questions, mocks and guides you see first.</p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
            {examCatalog.map((entry) => {
              const active = entry.id === familyId;
              return (
                <button
                  key={entry.id}
                  type="button"
                  onClick={() => {
                    setFamilyId(entry.id);
                    setPaperId(null);
                    setPaperSearch('');
                  }}
                  className={`text-left p-3.5 rounded-2xl border transition cursor-pointer ${
                    active ? 'bg-[#0E9594] border-[#0E9594] text-white shadow-md' : 'bg-white border-slate-200 hover:border-[#0E9594] text-slate-900'
                  }`}
                >
                  <div className="font-extrabold text-sm">{entry.shortLabel}</div>
                  <div className={`text-[11px] mt-0.5 ${active ? 'text-teal-50' : 'text-slate-500'}`}>{entry.state}</div>
                </button>
              );
            })}
          </div>

          {family && (
            <div className="space-y-3">
              <div className="text-sm font-extrabold text-slate-900">Now pick your exact exam</div>

              {familyPapers.length === 0 ? (
                <div className="bg-white border border-slate-200 rounded-2xl p-4 text-sm text-slate-600">
                  Practice content for {family.shortLabel} is on the way. We'll save {family.shortLabel} as your goal and show you
                  what's available now.
                </div>
              ) : (
                <>
                  {familyPapers.length > 8 && (
                    <div className="relative">
                      <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                      <input
                        type="text"
                        value={paperSearch}
                        onChange={(e) => setPaperSearch(e.target.value)}
                        placeholder="Search by code or name, e.g. 5733 or Biology"
                        className="w-full bg-white border border-slate-200 rounded-xl pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0E9594]"
                      />
                    </div>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 max-h-80 overflow-y-auto pr-1">
                    {filteredPapers.map((p) => {
                      const active = p.id === paperId;
                      const qCount = paperQuestionCount(p);
                      const domainCount = Array.isArray(p.domains) ? p.domains.length : 0;
                      return (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => setPaperId(p.id)}
                          className={`text-left p-3.5 rounded-2xl border transition cursor-pointer ${
                            active ? 'bg-[#0E9594] border-[#0E9594] text-white shadow-md' : 'bg-white border-slate-200 hover:border-[#0E9594]'
                          }`}
                        >
                          <div className={`text-xs font-black ${active ? 'text-teal-50' : 'text-[#0E9594]'}`}>{p.code}</div>
                          <div className={`text-sm font-bold leading-snug ${active ? 'text-white' : 'text-slate-900'}`}>{p.title}</div>
                          {Array.isArray(p.bundleOf) && p.bundleOf.length > 0 ? (
                            <div className={`text-[11px] mt-1 ${active ? 'text-teal-50' : 'text-slate-500'}`}>
                              Taken as {p.bundleOf.length} subtests ({p.bundleOf.join(', ')})
                            </div>
                          ) : (qCount > 0 || domainCount > 0) && (
                            <div className={`text-[11px] mt-1 ${active ? 'text-teal-50' : 'text-slate-500'}`}>
                              {qCount > 0 ? `${qCount} questions` : ''}
                              {qCount > 0 && domainCount > 0 ? ' · ' : ''}
                              {domainCount > 0 ? `${domainCount} domains` : ''}
                            </div>
                          )}
                        </button>
                      );
                    })}
                    {filteredPapers.length === 0 && (
                      <div className="text-sm text-slate-500 p-3">No exam matches that search.</div>
                    )}
                  </div>
                </>
              )}

              {selectedPaper && Array.isArray(selectedPaper.domains) && selectedPaper.domains.length > 0 && (
                <div className="bg-white border border-slate-200 rounded-2xl p-4 space-y-2.5">
                  <div className="text-[11px] font-black tracking-[0.15em] text-slate-500">{selectedPaper.code} COVERS</div>
                  <div className="flex flex-wrap gap-2">
                    {selectedPaper.domains.map((d: any, i: number) => (
                      <span key={`${d?.name}-${i}`} className="px-3 py-1.5 rounded-full bg-teal-50 border border-teal-200 text-teal-800 text-xs font-bold">
                        {d?.name}
                      </span>
                    ))}
                  </div>
                  {selectedPaper.durationMinutes > 0 && (
                    <div className="text-xs text-slate-500 flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5" /> {selectedPaper.durationMinutes} minutes on test day
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="flex flex-col-reverse sm:flex-row gap-3 pt-2">
            <button
              type="button"
              onClick={() => finish(true)}
              disabled={saving}
              className="sm:w-1/3 text-slate-500 hover:text-slate-800 font-bold text-sm py-3.5 rounded-2xl flex items-center justify-center gap-1.5 cursor-pointer disabled:opacity-50"
            >
              <SkipForward className="w-4 h-4" /> Skip for now
            </button>
            <button
              type="button"
              onClick={() => setStep('date')}
              disabled={!canContinueExam}
              className="flex-1 bg-[#0f1b4d] hover:bg-[#1b2f7a] text-white font-black text-sm py-3.5 rounded-2xl transition shadow-md flex items-center justify-center gap-2 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Continue <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {step === 'date' && (
        <div className="space-y-6">
          <div className="space-y-2">
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-[#0f1b4d] leading-tight">When's your exam?</h1>
            <p className="text-slate-600 text-sm sm:text-base">
              We'll build your study plan around this date. Not booked yet? Pick the date you're aiming for.
            </p>
          </div>

          <div className="grid grid-cols-[2fr_1fr_1.3fr] gap-2.5">
            <select
              value={month}
              disabled={noDateYet}
              onChange={(e) => setMonth(e.target.value)}
              className="bg-white border border-slate-200 rounded-xl px-3 py-3 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-[#0E9594] disabled:opacity-50"
            >
              <option value="">Month</option>
              {MONTHS.map((m, i) => (
                <option key={m} value={String(i + 1)}>{m}</option>
              ))}
            </select>
            <select
              value={day}
              disabled={noDateYet}
              onChange={(e) => setDay(e.target.value)}
              className="bg-white border border-slate-200 rounded-xl px-3 py-3 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-[#0E9594] disabled:opacity-50"
            >
              <option value="">Day</option>
              {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((d) => (
                <option key={d} value={String(d)}>{d}</option>
              ))}
            </select>
            <select
              value={year}
              disabled={noDateYet}
              onChange={(e) => setYear(e.target.value)}
              className="bg-white border border-slate-200 rounded-xl px-3 py-3 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-[#0E9594] disabled:opacity-50"
            >
              <option value="">Year</option>
              {yearOptions.map((y) => (
                <option key={y} value={String(y)}>{y}</option>
              ))}
            </select>
          </div>

          <label className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl px-4 py-3.5 cursor-pointer">
            <input
              type="checkbox"
              checked={noDateYet}
              onChange={(e) => setNoDateYet(e.target.checked)}
              className="w-4 h-4 accent-teal-600"
            />
            <span className="text-sm font-bold text-slate-800">I don't have an exam date yet</span>
          </label>

          {examDateInPast && (
            <div className="text-xs font-semibold text-rose-700 flex items-center gap-1.5">
              <AlertCircle className="w-4 h-4" /> That date has already passed. Please pick a future date.
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={() => setStep('exam')}
              className="w-1/3 bg-white border border-slate-200 text-slate-700 font-bold text-sm py-3.5 rounded-2xl flex items-center justify-center gap-1.5 cursor-pointer hover:bg-slate-50"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <button
              type="button"
              onClick={() => setStep('goal')}
              disabled={!canContinueDate}
              className="flex-1 bg-[#0f1b4d] hover:bg-[#1b2f7a] text-white font-black text-sm py-3.5 rounded-2xl transition shadow-md flex items-center justify-center gap-2 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Continue <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {step === 'goal' && (
        <div className="space-y-6">
          <div className="space-y-2">
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-[#0f1b4d] leading-tight">What's your daily study goal?</h1>
            <p className="text-slate-600 text-sm sm:text-base">You can always change this later. We'll track your progress against it.</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {DAILY_GOALS.map((g) => {
              const active = dailyGoal === g.minutes;
              return (
                <button
                  key={g.minutes}
                  type="button"
                  onClick={() => setDailyGoal(g.minutes)}
                  className={`py-5 rounded-2xl border text-base font-extrabold transition cursor-pointer ${
                    active ? 'bg-[#0E9594] border-[#0E9594] text-white shadow-md' : 'bg-white border-slate-200 text-slate-800 hover:border-[#0E9594]'
                  }`}
                >
                  {g.label}
                </button>
              );
            })}
          </div>

          {(selectedPaper || family) && (
            <div className="bg-white border border-slate-200 rounded-2xl p-4 text-sm text-slate-600 flex items-start gap-2.5">
              <CalendarDays className="w-4 h-4 text-[#0E9594] shrink-0 mt-0.5" />
              <span>
                Goal: <span className="font-bold text-slate-900">{selectedPaper ? `${selectedPaper.code} ${selectedPaper.title}` : family?.shortLabel}</span>
                {examDateIso && !noDateYet ? (
                  <> on <span className="font-bold text-slate-900">{MONTHS[Number(month) - 1]} {day}, {year}</span></>
                ) : null}
              </span>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={() => setStep('date')}
              disabled={saving}
              className="w-1/3 bg-white border border-slate-200 text-slate-700 font-bold text-sm py-3.5 rounded-2xl flex items-center justify-center gap-1.5 cursor-pointer hover:bg-slate-50 disabled:opacity-50"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <button
              type="button"
              onClick={() => finish(false)}
              disabled={!dailyGoal || saving}
              className="flex-1 bg-[#0f1b4d] hover:bg-[#1b2f7a] text-white font-black text-sm py-3.5 rounded-2xl transition shadow-md flex items-center justify-center gap-2 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {saving ? 'Building your plan...' : 'Build my study plan'}
              {!saving && <ArrowRight className="w-4 h-4" />}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
